import { normalizeOutput } from './normalize';

/** judge.md §4.3。 */
export type ChoiceResult = 'correct' | 'accepted' | 'wrong' | 'invalid';

/**
 * choice / complexity-quiz / reduce の採点（judge.md §4.3、vectors/choice-grading.json）。
 * options は YAML の並び順、selected はその添字（表示のシャッフルは戻してから渡す）。
 */
export function gradeChoice(
  options: readonly { correct: boolean; accepted?: boolean }[],
  multiple: boolean,
  selected: readonly number[],
): ChoiceResult {
  if (selected.some((i) => !Number.isInteger(i) || i < 0 || i >= options.length)) return 'invalid';
  if (!multiple) {
    if (selected.length !== 1) return 'invalid';
    const opt = options[selected[0] ?? -1];
    if (opt?.correct) return 'correct';
    if (opt?.accepted) return 'accepted';
    return 'wrong';
  }
  const chosen = new Set(selected);
  const want = new Set<number>();
  options.forEach((o, i) => {
    if (o.correct) want.add(i);
  });
  if (chosen.size !== want.size) return 'wrong';
  for (const i of chosen) if (!want.has(i)) return 'wrong';
  return 'correct';
}

/** Leitner で正解として扱うか（progress-store.md §4.3）。invalid は記録しないので null。 */
export function choiceResultToAnswer(r: ChoiceResult): 'correct' | 'wrong' | null {
  if (r === 'invalid') return null;
  return r === 'wrong' ? 'wrong' : 'correct';
}

/** predict-output の回答（judge.md §4）。固定の 2 択は compile-error / runtime-error。 */
export type PredictAnswer =
  { kind: 'output'; text: string } | { kind: 'compile-error' } | { kind: 'runtime-error' };

/** predict-output の採点（judge.md §4）。自由入力に NFKC は使わない。 */
export function gradePredictOutput(
  card: { outcome?: 'ok' | 'compile-error' | 'runtime-error'; expected?: string },
  answer: PredictAnswer,
): boolean {
  const outcome = card.outcome ?? 'ok';
  if (outcome !== 'ok') return answer.kind === outcome;
  return (
    answer.kind === 'output' &&
    normalizeOutput(answer.text) === normalizeOutput(card.expected ?? '')
  );
}

/** fill-blank の空欄 1 つの正規化（judge.md §4.1）。 */
export function normalizeBlank(s: string, match: 'trim' | 'ignore-spaces' = 'trim'): string {
  const n = s.normalize('NFKC');
  return match === 'ignore-spaces' ? n.replace(/\s+/g, '') : n.trim();
}

export interface BlankSpec {
  id: number;
  accept: readonly string[];
  match?: 'trim' | 'ignore-spaces';
}

/**
 * fill-blank の空欄照合（judge.md §4.1 手順 1〜2）。inputs は空欄 id → 入力。
 * allCorrect が false で grading が client-then-server なら {@link fillTemplate} の結果を実行する。
 */
export function gradeBlanks(
  blanks: readonly BlankSpec[],
  inputs: Readonly<Record<number, string>>,
): { allCorrect: boolean; perBlank: Record<number, boolean> } {
  const perBlank: Record<number, boolean> = {};
  for (const b of blanks) {
    const got = normalizeBlank(inputs[b.id] ?? '', b.match);
    perBlank[b.id] = b.accept.some((a) => normalizeBlank(a, b.match) === got);
  }
  return { allCorrect: blanks.every((b) => perBlank[b.id] === true), perBlank };
}

/** template の ⟦n⟧ を入力そのままで置き換える（judge.md §4.1 手順 3）。 */
export function fillTemplate(template: string, inputs: Readonly<Record<number, string>>): string {
  return template.replace(/⟦([1-9])⟧/g, (_, d: string) => inputs[Number(d)] ?? '');
}

const stripLineEnd = (line: string): string => line.replace(/[ \t]+$/, '');

/**
 * parsons の採点（judge.md §4.2）。回答は使った行の文字列の列。
 * 正解は lines と、alternatives を lines に当てた列のどれかと完全一致（行末空白は無視、インデントは比べる）。
 * distractors の行を 1 つでも使ったら不正解。
 */
export function gradeParsons(
  card: {
    lines: readonly string[];
    distractors?: readonly string[];
    alternatives?: readonly (readonly number[])[];
  },
  answer: readonly string[],
): boolean {
  const got = answer.map(stripLineEnd);
  const distractors = new Set((card.distractors ?? []).map(stripLineEnd));
  const lineSet = new Set(card.lines.map(stripLineEnd));
  if (got.some((l) => distractors.has(l) && !lineSet.has(l))) return false;
  const candidates: string[][] = [card.lines.map(stripLineEnd)];
  for (const alt of card.alternatives ?? []) {
    candidates.push(alt.map((i) => stripLineEnd(card.lines[i] ?? '')));
  }
  return candidates.some((c) => c.length === got.length && c.every((l, i) => l === got[i]));
}

/** viz-step の採点（judge.md §4）。全 checkpoint で answer と一致すれば正解。 */
export function gradeVizStep(
  checkpoints: readonly { answer: number }[],
  answers: readonly number[],
): boolean {
  return (
    checkpoints.length === answers.length && checkpoints.every((c, i) => answers[i] === c.answer)
  );
}
